import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import type { Contact as ContactType } from '@pbt/pbt-ui-components'

import Expander from '~/components/common/lists/Expander'
import DialogNames from '~/constants/DialogNames'
import {
  deleteContact,
  editContact,
  fetchContact,
  getContact,
  getContactsIsDeleting,
  getContactsIsFetching,
  getContactsIsUpdating,
} from '~/store/duck/contacts'
import type { DataHandleWithUnsavedChanges } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import Contact from './Contact'
import { isContactFormDirty } from './contactFormEquality'

export interface ContactDetailsProps {
  itemId: string
  onClose: () => void
}

const ContactDetails = ({ itemId, onClose }: ContactDetailsProps) => {
  const dispatch = useDispatch()
  const isDeleting = useSelector(getContactsIsDeleting)
  const isUpdating = useSelector(getContactsIsUpdating)
  const isFetching = useSelector(getContactsIsFetching)
  const contact = useSelector(getContact(itemId))
  const { t } = useTranslation(['Common'])

  const [isContactChanged, setIsContactChanged] = useState(false)

  const contactRef = useRef<DataHandleWithUnsavedChanges>(null)

  const [openContactDialog] = useDialog(DialogNames.CONTACT)

  const setCloseOnDelete = useCloseAfterCreation(onClose, getContactsIsDeleting)

  useEffect(() => {
    if (itemId) {
      dispatch(fetchContact(itemId))
    }
  }, [itemId])

  // Re-sync save button after save: LeaveConfirmationDialog uses live getUnsavedData(), but
  // Expander disables Save from isContactChanged, which useFieldsChanged does not always clear
  // after reset() when field signatures match the prior cache.
  useLayoutEffect(() => {
    if (!isUpdating && contact) {
      const storeContact = contact
      setIsContactChanged(
        Boolean(storeContact) &&
          !R.isEmpty(storeContact) &&
          isContactFormDirty(
            storeContact,
            contactRef.current?.get() ?? undefined,
          ),
      )
    }
  }, [contact, isUpdating])

  const getNewContact = () => contactRef.current?.get()

  // Same shape as SpaceDetails#getUnsavedData; contacts use snapshot equality because Redux
  // Contact carries extra fields vs the form (see contactFormEquality.ts).
  const getUnsavedData = () => {
    const newContact = getNewContact()
    return (
      Boolean(contact) &&
      !R.isEmpty(contact) &&
      isContactFormDirty(contact, newContact ?? undefined)
    )
  }

  const onSaveRequested = () => {
    if (contactRef.current?.validate() && isContactChanged) {
      const newContact = getNewContact()
      if (newContact) {
        dispatch(editContact(newContact))
      }
    }
  }

  const onDeleteRequested = () => {
    setCloseOnDelete()
    dispatch(deleteContact(itemId))
  }

  const onCloneRequested = () => {
    openContactDialog({
      contact: {
        ...contact,
        name: `${contact?.name ?? ''}_copy`,
      } as ContactType,
    })
  }

  const onChange = () => {
    setIsContactChanged(getUnsavedData())
  }

  return (
    <Expander
      isConfirmToDelete
      expandedItemClass={t('Common:CONTACT_ONE').toLowerCase()}
      getUnsavedData={getUnsavedData}
      hasUnsavedData={isContactChanged}
      isDeleting={isDeleting}
      isFetching={isFetching}
      isSaving={isUpdating}
      onBack={onClose}
      onCloneRequested={onCloneRequested}
      onDeleteRequested={onDeleteRequested}
      onSaveRequested={onSaveRequested}
    >
      <Contact contact={contact} ref={contactRef} onChange={onChange} />
    </Expander>
  )
}

export default ContactDetails
